/**
 * QR login panel for the TV: a logged-in phone scans the code and approves.
 *
 * After approval the TV claims regular login cookies with its device secret,
 * stores the pairing (HostPage reports the created session to the phone) and
 * sets the user — LoginPage's existing effect then navigates to returnUrl.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthStore } from '@/stores/authStore'
import { claimDevicePairing, startDevicePairing, ApiError } from '@/services/api'
import { connectPairingSocket, storePairing } from '@/services/pairingSocket'

export function QrLoginPanel() {
  const { t } = useTranslation()
  const setUser = useAuthStore((s) => s.setUser)
  // Incremented to start a fresh pairing after the previous one expired
  const [attempt, setAttempt] = useState(0)
  const [pairingCode, setPairingCode] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let disconnect: (() => void) | null = null
    let expiryTimer: ReturnType<typeof setTimeout> | null = null

    const claim = async (code: string, secret: string, ignoreConflict: boolean) => {
      try {
        const user = await claimDevicePairing(code, secret)
        if (cancelled) return
        storePairing(code)
        setUser(user)
      } catch (error) {
        if (cancelled) return
        const apiError = error as ApiError
        // On reconnect 409 just means "not approved yet"
        if (ignoreConflict && apiError.status === 409) return
        toast.error(apiError.message ?? t('auth.qrLogin.claimError'))
      }
    }

    startDevicePairing()
      .then(({ pairing_code, device_secret, expires_in_seconds }) => {
        if (cancelled) return
        let connectedOnce = false
        disconnect = connectPairingSocket(pairing_code, {
          onConnected: () => {
            if (cancelled) return
            if (!connectedOnce) {
              connectedOnce = true
              // Show QR only once the TV listens, so approval cannot outrun it
              setPairingCode(pairing_code)
              return
            }
            // pairing:approved may have been emitted while disconnected
            void claim(pairing_code, device_secret, true)
          },
          onApproved: () => {
            if (cancelled) return
            void claim(pairing_code, device_secret, false)
          },
          onSessionReady: () => {},
        })
        expiryTimer = setTimeout(() => {
          disconnect?.()
          disconnect = null
          setPairingCode(null)
          setAttempt((a) => a + 1)
        }, expires_in_seconds * 1000)
      })
      .catch((error: ApiError) => {
        if (cancelled) return
        toast.error(error.message ?? t('auth.qrLogin.startError'))
      })

    return () => {
      cancelled = true
      if (expiryTimer !== null) clearTimeout(expiryTimer)
      disconnect?.()
    }
  }, [attempt, setUser, t])

  return (
    <Card data-testid="qr-login-panel">
      <CardContent className="pt-6 flex flex-col items-center gap-4 text-center">
        <h2 className="text-lg font-display font-semibold">{t('auth.qrLogin.title')}</h2>
        <div className="bg-white p-3 rounded min-h-[232px] min-w-[232px] flex items-center justify-center">
          {pairingCode === null ? (
            <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
          ) : (
            <QRCode value={`${window.location.origin}/pair/${pairingCode}`} size={208} />
          )}
        </div>
        <p className="text-sm text-muted-foreground">{t('auth.qrLogin.hint')}</p>
      </CardContent>
    </Card>
  )
}
